import React from 'react';
import { Building2, Banknote, Coins, Layers3, Star, Home, Globe2, Wrench, Compass, ShieldAlert } from 'lucide-react';
import { useReveal } from '@/lib/useReveal';

/**
 * CapitalNetworkLoop — institutional NDA-safe marquee of capital partner
 * CATEGORIES (never named lenders). Two signature motions allowed by the
 * GP CAP Design System v1: (1) section-level scroll reveal, (2) ambient
 * marquee drift. Reduced motion freezes the track at frame 0.
 *
 * NDA-safe rules:
 *  - category tiles only — never a named lender / logo
 *  - tile set is rendered twice and the duplicate is aria-hidden, so the
 *    seamless loop never advertises the same content to screen readers
 *  - edge fade mask draws the eye toward the centre rather than the loop
 *    seams
 */

const CATEGORIES = [
  { label: 'Australian banks', icon: Building2 },
  { label: 'Non-bank lenders', icon: Banknote },
  { label: 'Private credit funds', icon: Coins },
  { label: 'Mezzanine providers', icon: Layers3 },
  { label: 'Preferred equity', icon: Star },
  { label: 'Family offices', icon: Home },
  { label: 'Offshore institutional', icon: Globe2 },
  { label: 'Construction financiers', icon: Wrench },
  { label: 'Bridging specialists', icon: Compass },
  { label: 'Special situations', icon: ShieldAlert },
];

const Tile = ({ Icon, label, ariaHidden }) => (
  <div
    className="gpc-network-tile shrink-0 flex items-center gap-2.5 rounded-md px-4 py-2.5 border"
    style={{
      background: 'var(--gpc-panel)',
      borderColor: 'var(--gpc-line)',
      color: 'var(--gpc-ink)',
    }}
    aria-hidden={ariaHidden || undefined}
  >
    <Icon className="w-4 h-4 shrink-0" style={{ color: 'var(--gpc-gold)' }} />
    <span className="text-[13px] font-medium whitespace-nowrap" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {label}
    </span>
  </div>
);

export const CapitalNetworkLoop = () => {
  const [ref, visible] = useReveal({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="gpc-section gpc-section--feature relative overflow-hidden"
      data-testid="capital-network-loop"
    >
      {/* Subtle masked grid — feature sections only per DS §4 */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.55] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(19,35,63,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(19,35,63,0.06) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(60% 70% at 50% 50%, #000 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(60% 70% at 50% 50%, #000 30%, transparent 80%)',
        }}
      />

      <div className={`gpc-container relative gpc-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="max-w-3xl mb-10">
          <p className="gpc-eyebrow mb-4">Capital network</p>
          <h2 className="gpc-h2 mb-4">
            Backed by a curated network of <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>institutional capital.</span>
          </h2>
          <p className="gpc-body max-w-2xl">
            Capital partners across banks, non-banks, private credit, mezzanine, preferred equity and family offices — matched to each mandate, not broadcast to a list.
          </p>
        </div>

        {/* Marquee — duplicated track, masked edges, pause-on-hover, reduced-motion safe */}
        <div className="gpc-marquee" role="region" aria-label="Categories of capital partners on the platform">
          <div className="gpc-marquee__track" data-testid="capital-network-marquee-track">
            {CATEGORIES.map((c) => (
              <Tile key={`a-${c.label}`} Icon={c.icon} label={c.label} />
            ))}
            {CATEGORIES.map((c) => (
              <Tile key={`b-${c.label}`} Icon={c.icon} label={c.label} ariaHidden />
            ))}
          </div>
        </div>

        <p
          className="mt-6 text-[12px]"
          style={{ color: 'var(--gpc-muted)', fontFamily: 'Space Grotesk, system-ui, sans-serif', letterSpacing: '0.06em' }}
          data-testid="capital-network-footer"
        >
          Representative of partner categories · specific lenders disclosed under NDA
        </p>
      </div>

      {/* Component-scoped styles for the marquee. Animation respects the DS:
            - transform-only (no layout shift)
            - linear, infinite, slow
            - pause on hover
            - reduced-motion freezes the track at frame 0 */}
      <style>{`
        .gpc-marquee {
          position: relative;
          overflow: hidden;
          mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
          -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
        }
        .gpc-marquee__track {
          display: flex;
          gap: 12px;
          width: max-content;
          animation: gpc-marquee 38s linear infinite;
          will-change: transform;
        }
        .gpc-marquee:hover .gpc-marquee__track {
          animation-play-state: paused;
        }
        .gpc-network-tile {
          transition: border-color 0.22s ease, background-color 0.22s ease;
        }
        .gpc-network-tile:hover {
          border-color: var(--gpc-line-strong);
          background: var(--gpc-paper-2) !important;
        }
        /* Translate by 50% — duplicate set means seam is invisible at 50% */
        @keyframes gpc-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .gpc-marquee__track { animation: none; transform: translateX(0); }
          .gpc-network-tile { transition: none; }
        }
      `}</style>
    </section>
  );
};

export default CapitalNetworkLoop;
